import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronDown, Trash2 } from "lucide-react";
import { api, errMsg, getToken, clearToken, money, fmtKST } from "@/lib/api";
import { EmptyState } from "@/components/Bits";
import { LateClaimRow } from "@/components/admin/LateClaimRow";
import { RestoreClaimDialog } from "@/components/admin/RestoreClaimDialog";

const Record = ({ r, onRemove, onWaive, onRestore }) => (
  <li data-testid={`past-due-record-${r.id}`} className="rounded-lg bg-zinc-950/60 px-3 py-2 text-sm">
   <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
    <span className="font-semibold min-w-0 break-words">{r.name}</span>
    {r.kind === "special" && <span className="rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-200">Special</span>}
    <span className="text-xs text-zinc-500">due {fmtKST(r.payment_due)}</span>
    <span className="ml-auto flex items-center gap-2">
      {r.resolved_at
        ? r.resolved_reason === "forfeited" && r.claims?.some((c) => c.restored_at) && !r.claims.some((c) => c.forfeited)
          ? <span data-testid={`past-due-restored-${r.id}`} className="text-xs text-sky-300">Claim restored</span>
          : r.resolved_reason === "forfeited"
          ? <span className="text-xs text-red-300">Claim dropped for non-payment</span>
          : r.resolved_reason === "waived"
          ? <span className="text-xs text-emerald-300">Late fee waived</span>
          : r.resolved_reason === "cleared"
          ? <span className="text-xs text-zinc-400">Cleared {fmtKST(r.resolved_at)} (due date or drop changed)</span>
          : <span className="text-xs text-emerald-300">Paid since {fmtKST(r.resolved_at)}</span>
        : <span className="text-xs font-semibold text-red-300">Unpaid {money(r.left)}</span>}
      <button type="button" data-testid={`past-due-remove-${r.id}`} onClick={() => onRemove(r)} aria-label="Remove record" className="p-1 rounded-lg text-zinc-500 hover:text-red-300 hover:bg-red-500/10 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
    </span>
   </div>
    {r.claims?.length > 0 && (
      <ul className="mt-2 space-y-1.5 border-t border-white/5 pt-2">{r.claims.map((c) => <LateClaimRow key={c.id} c={c} onWaive={onWaive} onRestore={onRestore} />)}</ul>
    )}
  </li>
);

const Offender = ({ o, onRemove, onWaive, onWaiveAll, onRestore }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`past-due-offender-${o.joiner_id}`} className={`rounded-2xl border p-4 ${o.current ? "border-red-500/40 bg-red-500/[0.06]" : "border-white/5 bg-zinc-900/60"}`}>
      <div className="flex flex-wrap items-center gap-3">
        <Link to={`/joiners/${o.joiner_id}`} className="font-semibold hover:text-violet-300 transition-colors">{o.name}</Link>
        <span data-testid={`past-due-count-${o.joiner_id}`} className="rounded-full bg-red-500/15 px-2.5 py-0.5 text-xs font-bold text-red-200">Past Due: {o.count}</span>
        {o.current > 0 && <span className="text-xs text-red-300">{o.current} still unpaid</span>}
        {o.waivable > 0 && (
          <button type="button" data-testid={`past-due-waive-all-${o.joiner_id}`} onClick={() => onWaiveAll(o)}
            className="ml-auto rounded-full border border-emerald-500/40 px-3 py-1 text-xs font-semibold text-emerald-200 transition-colors hover:bg-emerald-500/10">Waive all</button>
        )}
        <button type="button" data-testid={`past-due-toggle-${o.joiner_id}`} onClick={() => setOpen(!open)}
          className={`${o.waivable > 0 ? "" : "ml-auto "}inline-flex items-center gap-1 rounded-full border border-zinc-700 px-3 py-1 text-xs text-zinc-300 transition-colors hover:border-zinc-500`}>
          {open ? "Hide drops" : "See drops"} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && <ul className="mt-3 space-y-1.5">{o.records.map((r) => <Record key={r.id} r={r} onRemove={onRemove} onWaive={onWaive} onRestore={onRestore} />)}</ul>}
    </div>
  );
};

export default function PastDuePage() {
  const [authed, setAuthed] = useState(getToken() ? null : false);
  const [rows, setRows] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/past-due").then((r) => { setRows(r.data); setAuthed(true); }).catch(() => { clearToken(); setAuthed(false); });
  }, []);
  useEffect(() => {
    if (getToken()) load();
  }, [load]);
  const remove = async (r) => {
    if (!window.confirm(`Remove the past due record for ${r.name}?`)) return;
    try {
      await api.delete(`/admin/past-due/${r.id}`);
      toast.success("Record removed");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const waive = (c) => act(() => api.post("/admin/past-due/waive", { kind: c.kind, claim_id: c.id }), "Late fee waived");
  const waiveAll = (o) => window.confirm(`Waive every late fee for ${o.name}?`) && act(() => api.post("/admin/past-due/waive-all", { joiner_id: o.joiner_id }), "All late fees waived");
  if (authed === false) return <Navigate to="/admin" replace />;
  if (rows === null) return <div className="text-zinc-500 text-sm">Loading…</div>;
  return (
    <div className="rise" data-testid="past-due-page">
      <Link to="/admin" data-testid="back-to-admin" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"><ArrowLeft className="h-4 w-4" /> Admin</Link>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-4">Past Due Offenders</h1>
      <p className="text-zinc-400 mt-2 text-sm md:text-base max-w-2xl">Everyone who missed a payment due date. The count keeps every drop they were late on, even after they pay.</p>
      <div className="mt-8 space-y-3" data-testid="past-due-list">
        {rows.length === 0 && <EmptyState testId="past-due-empty" title="No one has been past due" hint="Joiners show up here once a payment due date passes without payment." />}
        {rows.map((o) => <Offender key={o.joiner_id} o={o} onRemove={remove} onWaive={waive} onWaiveAll={waiveAll} onRestore={setRestoring} />)}
      </div>
      <RestoreClaimDialog claim={restoring} onClose={() => setRestoring(null)} onDone={() => { setRestoring(null); load(); }} />
    </div>
  );
}
